import React, { ReactNode } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import Checkbox from '@adsk/alloy-react-checkbox';
import { useDeprecate } from '@adsk/alloy-react-helpers';
import { CheckmarkIcon } from '@adsk/alloy-react-icon';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

export type MenuOptionProps = StylableComponent<HTMLLIElement> & {
  isSelected?: boolean;
  isHighlighted?: boolean;
  isMultiple?: boolean;
  disabled?: boolean;
  /** @deprecated use disabled field instead */
  isDisabled?: boolean;
  text?: ReactNode;
  subtext?: ReactNode;
  renderLeftComponent?: () => ReactNode;
  renderRightComponent?: () => ReactNode;
};

const Label = styled.div<{
  isSelected?: boolean;
  disabled?: boolean;
}>`
  ${theme.typography.ellipsis}
  ${(props) =>
    props.isSelected && !props.disabled
      ? theme.typography.bodyMediumBold
      : theme.typography.bodyMedium}
  color: ${(props) => (props.disabled ? theme.colors.charcoal500 : 'inherit')};
  width: 100%;
`;

const Subtext = styled.div<{ disabled?: boolean }>`
  ${theme.typography.ellipsis}
  ${theme.typography.bodyMedium}
color: ${(props) =>
    props.disabled ? theme.colors.charcoal500 : theme.colors.charcoal700};
`;

type StyledLiProps = {
  disabled?: boolean;
  isSelected?: boolean;
  isMultiple?: boolean;
  isHighlighted?: boolean;
};

const StyledLi = styled.li<StyledLiProps>`
  display: flex;
  align-items: flex-start;
  padding: 8px 16px;
  cursor: ${(props) => (props.disabled ? undefined : 'pointer')};
  background-color: ${(props) =>
    props.isSelected && !props.isMultiple
      ? theme.colors._adskBlue100Alpha
      : props.isHighlighted && !props.disabled
      ? theme.colors.charcoal050
      : undefined};
  &:active {
    background-color: ${(props) =>
      props.disabled ? undefined : theme.colors.charcoal100};
  }
`;

const MenuItem = React.forwardRef<HTMLLIElement, MenuOptionProps>(
  (
    {
      isSelected,
      isMultiple,
      disabled,
      isDisabled,
      text: label,
      subtext,
      renderLeftComponent,
      renderRightComponent,
      style,
      ...props
    },
    ref
  ) => {
    useDeprecate(
      isDisabled !== undefined,
      'Dropdown MenuItem',
      'isDisabled',
      'disabled'
    );

    disabled = disabled !== undefined ? disabled : isDisabled;

    return (
      <StyledLi
        ref={ref}
        disabled={disabled}
        isSelected={isSelected}
        isMultiple={isMultiple}
        css={style}
        {...props}
      >
        {isMultiple && (
          <Checkbox
            checked={isSelected}
            style={{ marginRight: 8, marginTop: subtext ? 2 : 4 }}
            disabled={disabled}
            readOnly
          />
        )}
        {renderLeftComponent && renderLeftComponent()}
        <div
          css={{
            flex: 1,
            overflow: 'hidden',
            ...(!subtext && {
              display: 'flex',
              alignItems: 'center',
              minHeight: 24,
            }),
          }}
        >
          {label && (
            <Label disabled={disabled} isSelected={isSelected}>
              {label}
            </Label>
          )}
          {subtext && <Subtext disabled={disabled}>{subtext}</Subtext>}
        </div>
        {renderRightComponent
          ? renderRightComponent()
          : !disabled &&
            isSelected &&
            !isMultiple && (
              <CheckmarkIcon
                style={{
                  color: theme.colors.adskBlue700,
                  padding: '-2px, 0px',
                }}
              />
            )}
      </StyledLi>
    );
  }
);

MenuItem.displayName = 'MenuOption';

MenuItem.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Whether menu option is currently selected */
  isSelected: PropTypes.bool,
  /** Whether menu option is currently highlighted.  Used when cursor navigation */
  isHighlighted: PropTypes.bool,
  /** Whether menu option is in a multi select */
  isMultiple: PropTypes.bool,
  /** Whether menu option is disabled */
  disabled: PropTypes.bool,
  /** **DEPRECATED** use disabled field instead.  Whether menu option is disabled */
  isDisabled: PropTypes.bool,
  /** Primary content */
  text: PropTypes.node,
  /** Secondary content */
  subtext: PropTypes.node,
  /** component to render to the left of the item */
  renderLeftComponent: PropTypes.func,
  /** component to render to the right of the item */
  renderRightComponent: PropTypes.func,
};

export default MenuItem;
